/**
 * 1.17.2 — Offline-first API для invoices.
 *
 * Архитектура:
 *   • На сервере invoice_items — отдельная таблица с FK на invoices.
 *   • В Dexie храним Invoice с embedded `items: InvoiceItem[]`. Один
 *     документ — одна запись. Это упрощает чтение/запись на клиенте.
 *   • В outbox шлём всю запись целиком (`invoice_upsert`). Server-side
 *     handler делает upsert по invoice + replace invoice_items для этого
 *     invoice_id (delete-all-then-insert). Транзакционно безопасно
 *     потому что invoice "владеет" своими items.
 *
 * ID:
 *   • crypto.randomUUID() локально для invoice и каждого item.
 *   • Те же id уходят в Supabase. Upsert по id для идемпотентности.
 *
 * Состояния:
 *   • draft — диспетчер пред-создал, техник правит.
 *   • sent — выдан клиенту (печать / email).
 *   • paid — оплачен, payment_method заполнен, paid_at установлен.
 *   • cancelled — отменён.
 *
 * Все операции записи (create/update/markAsPaid/items CRUD/...) идут
 * через единую функцию `upsertInvoice` которая делает Dexie put +
 * outbox enqueue. Возвращает обновлённый Invoice сразу.
 */

import { isSupabaseConfigured, supabase } from '../auth/supabaseClient';
import { db } from '../sync/db';
import type {
  Invoice,
  InvoiceItem,
  PaymentMethod,
  CreateInvoiceItemPayload,
  CreateInvoicePaymentResponse,
} from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapInvoice(row: Record<string, unknown>): Invoice {
  return {
    id: row.id as string,
    order_id: row.order_id as string,
    company_id: row.company_id as string,
    created_by: row.created_by as string,
    status: row.status as Invoice['status'],
    payment_method: (row.payment_method as PaymentMethod) ?? null,
    total_amount: Number(row.total_amount ?? 0),
    paid_at: (row.paid_at as string) ?? null,
    created_at: row.created_at as string,
    updated_at: row.updated_at as string,
    items: Array.isArray(row.invoice_items)
      ? (row.invoice_items as Record<string, unknown>[]).map(mapItem)
      : Array.isArray(row.items)
        ? (row.items as Record<string, unknown>[]).map(mapItem)
        : undefined,
  };
}

function mapItem(row: Record<string, unknown>): InvoiceItem {
  return {
    id: row.id as string,
    invoice_id: row.invoice_id as string,
    title: row.title as string,
    quantity: Number(row.quantity),
    unit_price: Number(row.unit_price),
    total: Number(row.total),
  };
}

function computeTotal(items: InvoiceItem[]): number {
  return items.reduce((sum, it) => sum + (it.total ?? 0), 0);
}

/**
 * Универсальная функция для отправки изменённого invoice в Dexie + outbox.
 * Каждая мутация делает локальную копию, передаёт сюда — мы её сохраняем
 * и enqueue в outbox. Возврат: тот же invoice с актуальным updated_at.
 */
async function upsertInvoice(invoice: Invoice): Promise<Invoice> {
  const now = new Date().toISOString();
  const updated: Invoice = {
    ...invoice,
    updated_at: now,
    total_amount: computeTotal(invoice.items ?? []),
  };
  await db.invoices.put(updated);

  if (isSupabaseConfigured) {
    const { enqueueOutboxItem } = await import('../sync/outboxProcessor');
    await enqueueOutboxItem({
      type: 'invoice_upsert',
      // order_id для outbox UI (видно к какой заявке относится).
      order_id: updated.order_id,
      payload: { invoice: updated },
    });
  }

  return updated;
}

// ─── Invoice CRUD ─────────────────────────────────────────────────────────────

/** Create a draft invoice for a given order. Offline-first. */
export async function createInvoice(orderId: string, companyId: string, createdByUserId?: string | null): Promise<Invoice> {
  const now = new Date().toISOString();
  const invoice: Invoice = {
    id: crypto.randomUUID(),
    order_id: orderId,
    company_id: companyId,
    created_by: createdByUserId ?? '',
    status: 'draft',
    payment_method: null,
    total_amount: 0,
    paid_at: null,
    created_at: now,
    updated_at: now,
    items: [],
  };
  return upsertInvoice(invoice);
}

/** Fetch invoice with its items by invoice id. Dexie first, then server fallback. */
export async function fetchInvoice(invoiceId: string): Promise<Invoice | null> {
  const local = await db.invoices.get(invoiceId);
  if (local) return local;
  if (!isSupabaseConfigured || !navigator.onLine) return null;
  const { data, error } = await supabase
    .from('invoices')
    .select('*, invoice_items(*)')
    .eq('id', invoiceId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const invoice = mapInvoice(data as Record<string, unknown>);
  await db.invoices.put(invoice);
  return invoice;
}

/**
 * Fetch invoice by work order id. Dexie first. Если в локальном кэше
 * нет — fallback на сервер.
 */
export async function fetchInvoiceByOrderId(orderId: string): Promise<Invoice | null> {
  // Локальный поиск (для < 1000 invoices — мгновенно).
  const all = await db.invoices.where('order_id').equals(orderId).toArray();
  if (all.length > 0) {
    // Если несколько — берём самый свежий.
    all.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return all[0];
  }
  if (!isSupabaseConfigured || !navigator.onLine) return null;
  const { data, error } = await supabase
    .from('invoices')
    .select('*, invoice_items(*)')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const invoice = mapInvoice(data as Record<string, unknown>);
  await db.invoices.put(invoice);
  return invoice;
}

// ─── Invoice Items ─────────────────────────────────────────────────────────────

/** Add a line item to an invoice. Offline-first. */
export async function addInvoiceItem(
  invoiceId: string,
  payload: CreateInvoiceItemPayload,
): Promise<InvoiceItem> {
  const invoice = await db.invoices.get(invoiceId);
  if (!invoice) throw new Error('Счёт не найден в локальном кэше');
  const item: InvoiceItem = {
    id: crypto.randomUUID(),
    invoice_id: invoiceId,
    title: payload.title,
    quantity: payload.quantity,
    unit_price: payload.unit_price,
    total: payload.quantity * payload.unit_price,
  };
  const items = [...(invoice.items ?? []), item];
  await upsertInvoice({ ...invoice, items });
  return item;
}

/** Update a line item. Offline-first. */
export async function updateInvoiceItem(
  itemId: string,
  changes: Partial<CreateInvoiceItemPayload>,
): Promise<InvoiceItem> {
  // Найдём счёт содержащий этот item. Линейный поиск, для < 1000 invoices — OK.
  const all = await db.invoices.toArray();
  let foundInvoice: Invoice | null = null;
  let foundItem: InvoiceItem | null = null;
  for (const inv of all) {
    const it = (inv.items ?? []).find((i) => i.id === itemId);
    if (it) {
      foundInvoice = inv;
      foundItem = it;
      break;
    }
  }
  if (!foundInvoice || !foundItem) {
    throw new Error('Позиция счёта не найдена в локальном кэше');
  }
  const updatedItem: InvoiceItem = {
    ...foundItem,
    title: changes.title ?? foundItem.title,
    quantity: changes.quantity ?? foundItem.quantity,
    unit_price: changes.unit_price ?? foundItem.unit_price,
  };
  updatedItem.total = updatedItem.quantity * updatedItem.unit_price;
  const items = (foundInvoice.items ?? []).map((i) => i.id === itemId ? updatedItem : i);
  await upsertInvoice({ ...foundInvoice, items });
  return updatedItem;
}

/** Delete a line item. Offline-first. */
export async function deleteInvoiceItem(itemId: string): Promise<void> {
  const all = await db.invoices.toArray();
  for (const inv of all) {
    if ((inv.items ?? []).some((i) => i.id === itemId)) {
      const items = (inv.items ?? []).filter((i) => i.id !== itemId);
      await upsertInvoice({ ...inv, items });
      return;
    }
  }
  // Если в локальном кэше нет — silently ignore. На сервере может быть,
  // но без знания invoice_id мы не можем построить outbox корректно.
}

// ─── Status transitions ────────────────────────────────────────────────────────

/** Transition invoice to 'sent'. Offline-first. */
export async function markAsSent(invoiceId: string): Promise<void> {
  const invoice = await db.invoices.get(invoiceId);
  if (!invoice) throw new Error('Счёт не найден в локальном кэше');
  await upsertInvoice({ ...invoice, status: 'sent' });
}

/** Transition invoice to 'paid' and record payment method. Offline-first. */
export async function markAsPaid(invoiceId: string, method: PaymentMethod): Promise<void> {
  const invoice = await db.invoices.get(invoiceId);
  if (!invoice) throw new Error('Счёт не найден в локальном кэше');
  await upsertInvoice({
    ...invoice,
    status: 'paid',
    payment_method: method,
    paid_at: new Date().toISOString(),
  });
}

/**
 * Recalc and persist total_amount. Offline-first.
 * (В новой архитектуре `upsertInvoice` сам вычисляет total из items;
 *  эта функция теперь обёртка для совместимости со старыми вызовами.)
 */
export async function syncInvoiceTotal(invoiceId: string, _total: number): Promise<void> {
  const invoice = await db.invoices.get(invoiceId);
  if (!invoice) throw new Error('Счёт не найден в локальном кэше');
  await upsertInvoice(invoice); // total вычислится из items
}

/**
 * P0-1 stage 3b — auto-populate a draft invoice from order items.
 *
 * 1.17.2 — Offline-first. Никаких fail-silent блоков с network errors —
 * всё через Dexie + outbox.
 */
/**
 * 1.18.3 — Семантика двух invoice-фабрик в проекте:
 *
 *   • `ensureInvoiceFromItems` (этот файл) — низкоуровневая фабрика.
 *     Принимает уже подготовленный массив `{ title, price_amount, status }`.
 *     Если invoice для order_id уже существует — возвращает его не
 *     перезаписывая. Это контракт «never overwrite technician edits».
 *
 *   • `ensureInvoiceFromPlannedItems` (autoInvoice.ts) — высокоуровневый
 *     wrapper. Берёт WorkOrder, читает items, берёт `price_planned`
 *     (с fallback на price_amount для legacy), резолвит названия
 *     услуг → строит billable array → вызывает ensureInvoiceFromItems.
 *     Используется при «Начать выезд» и при первом открытии InvoiceScreen.
 *
 *   • Closing-time invoice в Completion.tsx — НЕ использует
 *     ensureInvoiceFromPlannedItems, потому что цены берутся из
 *     drafts (техник мог изменить фактическую цену). Конвертация
 *     там встроена в setupItemClose() и сразу зовёт ensureInvoiceFromItems
 *     с factual prices.
 *
 * Унификация ensureInvoiceFromItems и ensureInvoiceFromPlannedItems
 * в одну функцию рассматривалась в 1.18.3 — отказались: их разделение
 * следует bounded-context разделению (low-level фабрика vs.
 * order→items конвертер), и попытка объединить через `priceSource`
 * параметр приводит к более запутанной сигнатуре чем два честных
 * однозадачных API.
 */
export async function ensureInvoiceFromItems(
  orderId: string,
  companyId: string,
  items: Array<{
    title: string;
    price_amount: number;
    status: string;
  }>,
): Promise<Invoice | null> {
  const existing = await fetchInvoiceByOrderId(orderId);
  if (existing) return existing;

  const billableItems = items.filter(
    (i) => i.price_amount > 0 && i.status !== 'skipped',
  );
  if (billableItems.length === 0) return null;

  const now = new Date().toISOString();
  const invoiceId = crypto.randomUUID();
  const invoiceItems: InvoiceItem[] = billableItems.map((item) => ({
    id: crypto.randomUUID(),
    invoice_id: invoiceId,
    title: item.title,
    quantity: 1,
    unit_price: item.price_amount,
    total: item.price_amount,
  }));

  const invoice: Invoice = {
    id: invoiceId,
    order_id: orderId,
    company_id: companyId,
    created_by: '',
    status: 'draft',
    payment_method: null,
    total_amount: computeTotal(invoiceItems),
    paid_at: null,
    created_at: now,
    updated_at: now,
    items: invoiceItems,
  };

  return upsertInvoice(invoice);
}

/**
 * Прогрев invoices при логине. Incremental через cursor.
 *
 * Joined fetch с invoice_items — серверная сторона возвращает invoice
 * вместе с массивом его позиций. mapInvoice разворачивает обратно в
 * embedded `items`.
 */
export async function syncInvoicesFromServer(companyId: string): Promise<void> {
  if (!isSupabaseConfigured || !navigator.onLine) return;
  const { incrementalSyncTable } = await import('../sync/incrementalSync');
  await incrementalSyncTable<Invoice>({
    entity: 'invoices',
    companyId,
    tableName: 'invoices',
    selectClause: '*, invoice_items(*)',
    applyBatch: async (rows) => {
      const invoices = (rows as unknown[])
        .map((r) => mapInvoice(r as Record<string, unknown>))
        // 1.18.2 — RLS guard.
        .filter((inv) => inv.company_id === companyId);
      if (invoices.length > 0) await db.invoices.bulkPut(invoices);
      return invoices;
    },
  });
}

// ─── Public company info (for InvoicePage — no auth required) ─────────────────

/**
 * Имя клиента (заказчика) для публичной страницы счёта/акта.
 * Через узкую security-definer RPC (отдаёт только имя, и только для non-draft
 * счёта). Пусто — если счёт draft, имя не задано или сети нет.
 */
export async function fetchInvoiceClientName(invoiceId: string): Promise<string> {
  if (!isSupabaseConfigured) return '';
  try {
    const { data } = await supabase.rpc('get_invoice_client_name', { p_invoice_id: invoiceId });
    return typeof data === 'string' ? data : '';
  } catch {
    return '';
  }
}

export interface CompanyBranding {
  stamp_url: string | null;
  signature_url: string | null;
}

/**
 * Печать и подпись компании для счёта/акта. Читаем напрямую из company_details
 * (минуя Dexie-кэш PublicCompanyInfo — это редкий разовый запрос на странице
 * документа, кэшировать его смысла нет). Публичное чтение разрешено политикой.
 */
export async function fetchCompanyBranding(companyId: string): Promise<CompanyBranding> {
  if (!isSupabaseConfigured) return { stamp_url: null, signature_url: null };
  try {
    const { data } = await supabase
      .from('company_details')
      .select('stamp_url, signature_url')
      .eq('company_id', companyId)
      .maybeSingle();
    const row = (data ?? {}) as Record<string, unknown>;
    return {
      stamp_url: (row.stamp_url as string) ?? null,
      signature_url: (row.signature_url as string) ?? null,
    };
  } catch {
    return { stamp_url: null, signature_url: null };
  }
}

export interface PublicCompanyInfo {
  name: string;
  logo_url: string;
  phone: string;
  email: string;
  payment_details: {
    bank_name: string;
    account: string;
    bik: string;
    inn: string;
    kpp: string;
    sbp_phone: string;
  };
}

/**
 * 1.19.8 — Offline-first чтение реквизитов компании.
 *
 * Стратегия:
 *   1. Сначала пытаемся вернуть из Dexie-кэша company_info. Это даёт
 *      мгновенный ответ для PaymentScreen даже без сети.
 *   2. Если в кэше есть запись — возвращаем её. Параллельно (fire-and-
 *      forget) запускаем фоновое обновление с сервера, если онлайн.
 *      Свежий снимок попадёт в Dexie к следующему вызову.
 *   3. Если в кэше пусто, а сеть есть — синхронно тянем с сервера,
 *      кладём в кэш, возвращаем.
 *   4. Если в кэше пусто и сети нет — возвращаем null. Это единственный
 *      случай, когда UI увидит «реквизиты не указаны».
 *
 * Вызов hydrateCompanyInfo(companyId) при логине гарантирует, что к
 * первому открытию PaymentScreen кэш уже заполнен.
 */
export async function fetchPublicCompanyInfo(companyId: string): Promise<PublicCompanyInfo | null> {
  // Попытка 1: Dexie cache.
  const cached = await db.company_info.get(companyId).catch(() => null);

  if (cached) {
    // Возвращаем кэш мгновенно. В фоне обновляем с сервера, если онлайн.
    if (isSupabaseConfigured && navigator.onLine) {
      void refreshCompanyInfoFromServer(companyId);
    }
    return {
      name: cached.name,
      logo_url: cached.logo_url,
      phone: cached.phone,
      email: cached.email,
      payment_details: { ...cached.payment_details },
    };
  }

  // Попытка 2: синхронный fetch с сервера.
  if (!isSupabaseConfigured || !navigator.onLine) return null;
  return refreshCompanyInfoFromServer(companyId);
}

/**
 * 1.32.0 — Тянет PublicCompanyInfo с сервера, кладёт в Dexie, возвращает.
 *
 * Источник данных:
 *   • companies.name — название компании (источник правды)
 *   • company_details.* — все остальные поля (логотип, контакты, банк)
 *
 * Раньше (до 1.32.0) название/контакты/логотип/payment_details лежали
 * в company_settings, разделение между техническими и карточными
 * данными отсутствовало. С 1.32.0 карточные данные переехали в
 * company_details, технические остались в company_settings.
 *
 * Используется и как фоновое обновление (после возврата кэша), и как
 * первый fetch (когда кэш пуст). Идемпотентна: повторный вызов
 * перезаписывает rec в company_info по company_id (PK).
 *
 * Безопасна к ошибкам: при сетевой проблеме или RLS-отказе возвращает
 * null, не бросая исключение.
 */
async function refreshCompanyInfoFromServer(companyId: string): Promise<PublicCompanyInfo | null> {
  if (!isSupabaseConfigured || !navigator.onLine) return null;
  try {
    // Запрос идёт двумя параллельными ветками: название из companies
    // и реквизиты из company_details. Обе ветки могут вернуть null
    // (новая компания без заполненных реквизитов) — в этом случае
    // соответствующие поля остаются пустыми строками.
    const [companyResp, detailsResp] = await Promise.all([
      supabase.from('companies').select('name').eq('id', companyId).maybeSingle(),
      supabase
        .from('company_details')
        .select('logo_url, contact_phone, contact_email, bank_name, bank_account, bank_bic, inn, kpp, sbp_phone')
        .eq('company_id', companyId)
        .maybeSingle(),
    ]);

    if (companyResp.error) return null;
    const company = (companyResp.data ?? {}) as Record<string, unknown>;
    const details = (detailsResp.data ?? {}) as Record<string, unknown>;

    const info: PublicCompanyInfo = {
      name:      String(company.name           ?? ''),
      logo_url:  String(details.logo_url       ?? ''),
      phone:     String(details.contact_phone  ?? ''),
      email:     String(details.contact_email  ?? ''),
      payment_details: {
        bank_name: String(details.bank_name    ?? ''),
        account:   String(details.bank_account ?? ''),
        bik:       String(details.bank_bic     ?? ''),
        inn:       String(details.inn          ?? ''),
        kpp:       String(details.kpp          ?? ''),
        sbp_phone: String(details.sbp_phone    ?? ''),
      },
    };

    // Пишем в кэш для будущих офлайн-вызовов.
    await db.company_info.put({
      company_id: companyId,
      name: info.name,
      logo_url: info.logo_url,
      phone: info.phone,
      email: info.email,
      payment_details: { ...info.payment_details },
      updated_at: new Date().toISOString(),
    }).catch(() => { /* silent — кэш best-effort */ });

    return info;
  } catch {
    return null;
  }
}

/**
 * 1.19.8 — Hydration реквизитов компании. Вызывается при логине, чтобы
 * Dexie-кэш был готов к первой оплате. Если уже что-то закэшировано —
 * перезаписываем свежими данными.
 *
 * Безопасна к ошибкам — никогда не бросает, не блокирует логин при
 * проблеме с сетью или RLS.
 */
export async function hydrateCompanyInfo(companyId: string): Promise<void> {
  if (!companyId) return;
  await refreshCompanyInfoFromServer(companyId);
}

// ─── 1.25.0 — P0-1: online payment via YooKassa ──────────────────────────────
//
// `markAsPaid()` выше — для офлайн-методов (наличные, перевод, СБП-QR).
// Эта функция — для онлайн-оплаты картой через ЮKassa. Она НЕ помечает
// invoice как paid локально: статус ставится только webhook'ом на сервере
// после реального успешного списания. Клиентская сторона ждёт через
// incremental sync (Dexie подтянет paid через несколько секунд).
//
// Офлайн:
//   • Функция требует online (зовётся через supabase.functions.invoke).
//   • При отсутствии сети — кидает ошибку, UI должен это перехватить
//     и предложить наличные/перевод/QR.
//   • idempotency_key обязателен — генерируется в UI один раз при
//     открытии формы оплаты, чтобы повторный клик не создал второй
//     платёж в ЮKassa.
//
// Stub-режим:
//   • Если на сервере не заданы YOOKASSA_SHOP_ID/SECRET_KEY, функция
//     вернёт HTTP 503 с `stub: true`. Мы конвертируем это в обычную
//     ошибку, UI ловит и показывает понятное сообщение.
//   • Это нормальное состояние пилота до регистрации ИП/ООО — UI
//     не должен показывать кнопку «Оплатить онлайн» вообще (или
//     показывать её disabled). Окончательно UX закроет P0-3.

export async function createOnlinePayment(input: {
  invoice_id: string;
  idempotency_key: string;
  return_url?: string;
}): Promise<CreateInvoicePaymentResponse> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Онлайн-оплата недоступна: Supabase не настроен');
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Онлайн-оплата требует подключения к интернету. Используйте наличные, перевод или СБП-QR.');
  }

  const { data, error } = await supabase.functions.invoke('create-invoice-payment', {
    body: input,
  });

  // ─── Non-2xx ответ: SDK завернул в error, тело надо прочитать вручную ──
  // 1.26.0 hotfix: supabase-js v2 на non-2xx (включая 503 stub) кидает
  // FunctionsHttpError. Тело ответа лежит в error.context (это Response).
  // Если не развернуть — пользователь увидит "non-2xx status code"
  // вместо понятного "Онлайн-оплата временно недоступна".
  if (error) {
    let serverBody: CreateInvoicePaymentResponse | null = null;
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        serverBody = await ctx.clone().json() as CreateInvoicePaymentResponse;
      } catch {
        serverBody = null;
      }
    }

    if (serverBody?.stub) {
      throw new Error(serverBody.error ?? 'Онлайн-оплата временно недоступна');
    }
    throw new Error(serverBody?.error ?? error.message ?? 'Не удалось создать платёж');
  }

  const response = data as CreateInvoicePaymentResponse;

  // 2xx с stub — на всякий случай (теоретически не приходит, 503 от сервера)
  if (response.stub) {
    throw new Error(response.error ?? 'Онлайн-оплата временно недоступна');
  }

  if (!response.success) {
    throw new Error(response.error ?? 'Не удалось создать платёж');
  }

  return response;
}
